import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle, PhoneCall, Save, MessageSquare } from "lucide-react";
import { format } from "date-fns";

const QAWorkdeskPage = () => {
  const { authAxios, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("calls");
  const [pendingItems, setPendingItems] = useState([]);
  const [matrices, setMatrices] = useState([]);
  
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedMatrix, setSelectedMatrix] = useState("");
  const [score, setScore] = useState("");
  const [comments, setComments] = useState("");
  const [callCategory, setCallCategory] = useState("");
  const [failureReason, setFailureReason] = useState("");
  const [failureArea, setFailureArea] = useState("");

  const fetchData = async (channel = activeTab) => {
    try {
      setLoading(true);
      const [pendingRes, matricesRes] = await Promise.all([
        authAxios.get(`/qa/pending?channel=${channel}&limit=20`),
        authAxios.get("/matrices")
      ]);
      setPendingItems(pendingRes.data);
      setMatrices(matricesRes.data);
    } catch (error) {
      console.error("Error fetching QA data:", error);
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEvaluate = async () => {
    if (!selectedItem || !selectedMatrix || !score) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }

    const matrix = matrices.find(m => m.id === selectedMatrix);

    try {
      await authAxios.post(`/qa/evaluate/${selectedItem.id}?channel=${activeTab}`, {
        matrix_id: selectedMatrix,
        matrix_name: matrix?.name || "Matriz Manual",
        evaluator_id: user.id || "admin",
        evaluator_name: user.name || "Admin",
        score: parseFloat(score),
        comments: comments,
        call_category: callCategory || null,
        failure_reason: failureReason || null,
        failure_area: failureArea || null
      });

      toast.success(`${activeTab === "chats" ? "Chat" : "Llamada"} evaluada correctamente`);
      setSelectedItem(null);
      setSelectedMatrix("");
      setScore("");
      setComments("");
      setCallCategory("");
      setFailureReason("");
      setFailureArea("");
      fetchData(activeTab); // Refresh list
    } catch (error) {
      console.error("Error saving evaluation:", error);
      toast.error("Error al guardar evaluación");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Workdesk QA</h1>
        <p className="text-slate-500 mt-1">Evalúa interacciones (llamadas y chats) manualmente y gestiona tu trabajo de calidad.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Pending List & Tab Toggle */}
        <div className="lg:col-span-1 space-y-4">
          <div className="flex border-b border-slate-200 bg-slate-100/80 p-1 rounded-lg">
            <Button
              variant={activeTab === "calls" ? "default" : "ghost"}
              size="sm"
              onClick={() => {
                setActiveTab("calls");
                setSelectedItem(null);
                fetchData("calls");
              }}
              className={`flex-1 text-xs font-semibold ${activeTab === "calls" ? "bg-white text-slate-900 shadow-sm hover:bg-white" : "text-slate-500"}`}
            >
              <PhoneCall className="w-3.5 h-3.5 mr-1.5" />
              Llamadas
            </Button>
            <Button
              variant={activeTab === "chats" ? "default" : "ghost"}
              size="sm"
              onClick={() => {
                setActiveTab("chats");
                setSelectedItem(null);
                fetchData("chats");
              }}
              className={`flex-1 text-xs font-semibold ${activeTab === "chats" ? "bg-white text-slate-900 shadow-sm hover:bg-white" : "text-slate-500"}`}
            >
              <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
              Chats
            </Button>
          </div>

          <h2 className="text-lg font-semibold flex items-center gap-2 text-slate-700">
            <CheckCircle className="w-4 h-4 text-sky-600" />
            Pendientes de Evaluar ({pendingItems.length})
          </h2>
          
          <div className="space-y-3 max-h-[550px] overflow-y-auto pr-2">
            {pendingItems.length === 0 ? (
              <p className="text-sm text-slate-500 p-4 border rounded bg-slate-50">No hay registros pendientes de evaluar.</p>
            ) : (
              pendingItems.map(item => (
                <Card 
                  key={item.id} 
                  className={`cursor-pointer transition-all hover:border-sky-500 ${selectedItem?.id === item.id ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-500' : ''}`}
                  onClick={() => setSelectedItem(item)}
                >
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-semibold text-slate-900 truncate max-w-[120px]">{item.agent || "Agente Desconocido"}</span>
                      <span className="text-xs text-slate-500 shrink-0">
                        {item.call_date ? format(new Date(item.call_date), "dd MMM HH:mm") : "-"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      {activeTab === "calls" ? <PhoneCall className="w-3 h-3 text-sky-500" /> : <MessageSquare className="w-3 h-3 text-emerald-500" />}
                      <span className="truncate">{item.phone_number || "Sin número"}</span>
                    </div>
                    <div className="mt-2 text-xs text-slate-500 line-clamp-2 italic">
                      {item.summary || "Sin detalles de transcripción."}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>

        {/* Evaluation Form */}
        <div className="lg:col-span-2">
          {selectedItem ? (
            <Card>
              <CardHeader>
                <CardTitle>Evaluación de {activeTab === "chats" ? "Chat" : "Llamada"}</CardTitle>
                <div className="text-sm text-slate-500">
                  Agente: <strong>{selectedItem.agent || "Desconocido"}</strong> | ID: <span className="font-mono text-xs">{selectedItem.id}</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <label className="text-sm font-medium mb-1 block">Matriz de Calidad *</label>
                  <select 
                    className="w-full border-slate-200 rounded-md p-2 border focus:ring-1 focus:ring-sky-500 focus:outline-none text-sm"
                    value={selectedMatrix}
                    onChange={(e) => setSelectedMatrix(e.target.value)}
                  >
                    <option value="">Selecciona una matriz...</option>
                    {matrices.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Calificación Final (0-100) *</label>
                  <Input 
                    type="number" 
                    min="0" max="100" 
                    placeholder="Ej. 95" 
                    value={score}
                    onChange={(e) => setScore(e.target.value)}
                  />
                  <p className="text-xs text-slate-500 mt-1">Calificación global asignada a la atención del agente.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Motivo de la Interacción</label>
                    <select
                      className="w-full border-slate-200 rounded-md p-2 border focus:ring-1 focus:ring-sky-500 focus:outline-none text-sm"
                      value={callCategory}
                      onChange={(e) => setCallCategory(e.target.value)}
                    >
                      <option value="">Selecciona motivo...</option>
                      <option value="nuevos servicios">Nuevos Servicios</option>
                      <option value="tracking">Tracking / Rastreo</option>
                      <option value="falla en alguna plataforma">Falla en Plataforma</option>
                      <option value="no comprende el proceso">No Comprende el Proceso</option>
                      <option value="falla en el proceso">Falla en el Proceso</option>
                      <option value="error operativo">Error Operativo</option>
                      <option value="nuevos negocios (envios, compras)">Nuevos Negocios (envíos, compras)</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm font-medium mb-1 block font-semibold text-rose-700">Causa Raíz de Falla (si aplica)</label>
                    <select
                      className="w-full border-slate-200 rounded-md p-2 border focus:ring-1 focus:ring-sky-500 focus:outline-none text-sm text-rose-800"
                      value={failureReason}
                      onChange={(e) => setFailureReason(e.target.value)}
                    >
                      <option value="">No aplica / Sin fallas</option>
                      <option value="falta de capacitación">Falta de Capacitación</option>
                      <option value="aptitud">Aptitud (Habilidad Operativa)</option>
                      <option value="actitud">Actitud (Calidad en Trato)</option>
                      <option value="otro">Otro</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Área de Falla Constante</label>
                  <Input 
                    placeholder="Ej. Procesos de Aduana, Facturación, etc." 
                    value={failureArea}
                    onChange={(e) => setFailureArea(e.target.value)}
                  />
                  <p className="text-xs text-slate-500 mt-1">Escribe el área o aspecto específico donde el agente presenta fallas recurrentes.</p>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Comentarios del Supervisor</label>
                  <Textarea 
                    placeholder="Escribe aquí las oportunidades de mejora, felicitaciones o notas específicas sobre esta interacción..."
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    rows={4}
                  />
                </div>

                <div className="flex justify-end pt-4 border-t">
                  <Button onClick={handleEvaluate} className="bg-sky-600 hover:bg-sky-700">
                    <Save className="w-4 h-4 mr-2" />
                    Guardar Evaluación
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="h-full flex items-center justify-center border-2 border-dashed border-slate-200 rounded-xl bg-slate-50/50 p-8 text-center min-h-[400px]">
              <div>
                <CheckCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-slate-900">Selecciona una interacción</h3>
                <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
                  Haz clic en uno de los registros de la lista de pendientes para comenzar a evaluar.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QAWorkdeskPage;
